import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import useIgnorableError from '@ecp/common/src/hooks/useIgnorableError';
import { overrideProperties } from '@ecp/common/src/style/utils';
import { sizePropTypes } from '@ecp/common/src/style/propTypes';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import RadioGroupUnstyled from '@ecp/common/src/components/unstyled/input/RadioGroupUnstyled';
import { StyledErrorMessage } from '@ecp/common/src/components/input/bo/Input';
import { Radio } from '@ecp/common/src/components/radio/bo/Radio';

const RadioGroup = React.forwardRef(
  ({ children, error, errorMessage, itemList, itemComponent: RadioComponent, onChange, ...props }, ref) => {
    const [selectedValue, setSelectedValue] = useState();
    const { ignoreError } = useIgnorableError({ error, value: selectedValue });

    const handleRadioChange = useCallback(
      (event) => {
        setSelectedValue(event?.target?.value);
        onChange?.(event);
      },
      [onChange]
    );

    return (
      <Container>
        <RadioGroupUnstyled ref={ref} rootComponent={StyledRadioGroupWrapper} onChange={handleRadioChange} {...props}>
          {children}
          {itemList?.map(({ value, label }, index) => (
            <RadioComponent key={`radio-group-item__${index}`} value={value} disabled={props.disabled}>
              {label}
            </RadioComponent>
          ))}
        </RadioGroupUnstyled>
        {!ignoreError && error && errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      </Container>
    );
  }
);

const Container = styled(FlexBoxColumn)`
  align-self: stretch;
  align-items: flex-start;
  justify-content: center;
`;

const StyledRadioGroupWrapper = styled(FlexBox)`
  ${overrideProperties(sizePropTypes, {
    height: 'fit-content',
    width: 'fit-content',
  })};
`;

const StyledRadio = styled(Radio)`
  padding-right: 14px;
`;

RadioGroup.displayName = 'RadioGroup';

RadioGroup.propTypes = {
  children: PropTypes.node,
  props: PropTypes.object,
  gap: PropTypes.string,
  ...sizePropTypes,
  itemComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  itemList: PropTypes.array,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
};

RadioGroup.defaultProps = {
  height: 'fit-content',
  width: 'fit-content',
  gap: '0',
  itemList: [],
  itemComponent: StyledRadio,
};

export default RadioGroup;
